<script setup lang="ts">
import { useNews } from '~/composables/useNews'
import { useAgenda } from '~/composables/useAgenda'

const { getAllNews, getAllNewsYears, getNewsCategories } = useNews()
const { getUpcomingEvents, filterEventsThisWeek, filterEventsSoon } = useAgenda()
const { getMapsUrl } = useMaps()

// --- State: News ---
const page = ref(1)
const pageSize = 12
const selectedYear = ref<number | undefined>(undefined)
const newsItems = ref<any[]>([])
const totalPages = ref(0)
const loadingNews = ref(false)
const newsYears = await getAllNewsYears()

const loadNews = async (reset = false) => {
  loadingNews.value = true
  if (reset) {
    page.value = 1
    newsItems.value = []
  }
  const result = await getAllNews(page.value, pageSize, selectedYear.value)
  newsItems.value = [...newsItems.value, ...result.items]
  totalPages.value = result.totalPages
  loadingNews.value = false
}

// Initial News Load
await loadNews()

// Categories (computed from loaded news for now, but ideally from API if we want global cats)
const categories = computed(() => getNewsCategories(newsItems.value))
const activeCategory = ref('Toutes')

const filteredNews = computed(() => {
  if (activeCategory.value === 'Toutes') return newsItems.value
  return newsItems.value.filter(item => item.category === activeCategory.value)
})

// --- State: Agenda ---
const { data: allEvents } = await useAsyncData('all-events', () => getUpcomingEvents())
const activeAgendaFilter = ref('Tous') // Tous, Cette semaine, Prochainement
const showArchives = ref(false)

const agendaFilters = computed(() => {
  if (!allEvents.value) return ['Tous']
  
  const filters = ['Tous']
  
  // Check "Cette semaine"
  if (filterEventsThisWeek(allEvents.value).length > 0) {
    filters.push('Cette semaine')
  }
  
  // Check "Prochainement"
  if (filterEventsSoon(allEvents.value).length > 0) {
    filters.push('Prochainement')
  }
  
  return filters
})

const filteredEvents = computed(() => {
  if (!allEvents.value) return []
  let filtered = [...allEvents.value]
  
  if (showArchives.value) {
    // Archives logic: strictly past days (yesterday and before)
    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    filtered = filtered.filter(e => new Date(e.dateRaw).getTime() < todayStart.getTime())
    
    if (selectedArchiveYear.value) {
      filtered = filtered.filter(e => new Date(e.dateRaw).getFullYear() === selectedArchiveYear.value)
    }
    if (selectedArchiveMonth.value !== undefined) {
      filtered = filtered.filter(e => new Date(e.dateRaw).getMonth() === selectedArchiveMonth.value)
    }
  } else {
    // Current events logic: today and future
    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    
    // If event has end date, use it. Otherwise use start date.
    filtered = filtered.filter(e => {
        if (e.dateEndRaw) {
            return new Date(e.dateEndRaw).getTime() >= new Date().getTime() // Ongoing (end date not passed)
        }
        return new Date(e.dateRaw).getTime() >= todayStart.getTime() // Started today or later
    })
    
    if (activeAgendaFilter.value === 'Cette semaine') {
      filtered = filterEventsThisWeek(filtered)
    } else if (activeAgendaFilter.value === 'Prochainement') {
      filtered = filterEventsSoon(filtered)
    }
  }
  
  return filtered
})

// --- Archive Filters State ---
const selectedArchiveYear = ref<number | undefined>(new Date().getFullYear())
const selectedArchiveMonth = ref<number | undefined>(undefined)
const archiveYears = computed(() => {
  const current = new Date().getFullYear()
  return Array.from({ length: current - 2024 + 1 }, (_, i) => 2024 + i).reverse()
})
const months = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
]

// --- Helpers ---
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Masterclass': return 'bg-purple-700 text-white'
    case 'Événement': return 'bg-oxo-red text-white'
    case 'Atelier': return 'bg-blue-700 text-white'
    case 'JPO': return 'bg-green-700 text-white'
    case 'Conférence': return 'bg-oxo-red text-white'
    default: return 'bg-oxo-yellow text-slate-900'
  }
}

const changeYear = (year: number | undefined) => {
  selectedYear.value = year
  loadNews(true)
}

const loadMoreNews = () => {
  if (page.value < totalPages.value) {
    page.value++
    loadNews()
  }
}

const toggleArchives = () => {
  showArchives.value = !showArchives.value
  activeAgendaFilter.value = 'Tous'
}

// Logic for custom date display (Start-End in square)
const getEventDateParts = (event: any) => {
  const startParts = event.date.split(' ')
  const startDay = startParts[0]
  const startMonth = startParts[1]

  if (event.endDateFormatted) {
    const endParts = event.endDateFormatted.split(' ')
    const endDay = endParts[0]
    const endMonth = endParts[1]

    if (startMonth.toLowerCase() === endMonth.toLowerCase() && startDay !== endDay) {
       return {
         day: `${startDay}-${endDay}`,
         month: startMonth,
         isRange: true
       }
    }
  }
  return {
    day: startDay,
    month: startMonth,
    isRange: false
  }
}


const { setPageSeo } = useSeo()

await setPageSeo({
  title: 'Actualités & Événements',
  description: 'Retrouvez toutes les actualités, événements et masterclasses de l\'école OXO.'
})
// Utility to strip HTML from event descriptions for card display
const stripHtml = (html: string) => {
  if (!html) return ''
  // Replace block tags with space to ensure separation, then remove all tags
  return html.replace(/<\/?[^>]+(>|$)/g, ' ').replace(/\s+/g, ' ').trim()
}
</script>

<template>
  <div class="bg-background text-foreground min-h-screen transition-colors duration-300">
    <UiPageHero 
      pageKey="news"
      badge-text="Blog & Événements"
      title-prefix="L'ÉCOLE EN"
      title-highlight="MOUVEMENT"
      description="Ne manquez aucun rendez-vous et restez informé des dernières tendances du jeu vidéo."
      :tags="['ÉVÉNEMENTS', 'INFOS SECTEUR', 'OXO']"
      height="screen"
      align="left"
      :primary-button="{ text: 'NOUS CONTACTER', to: '/contact', umamiEvent: 'News - Hero - Contact' }"
      :secondary-button="{ text: 'VOIR L\'AGENDA', to: '#agenda', umamiEvent: 'News - Hero - Agenda' }"
    />

    <!-- SECTION: AGENDA -->
    <section id="agenda" class="py-section-py relative overflow-hidden" aria-label="Agenda de l'école">
      <div class="container mx-auto px-container-px">
        <header class="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <UiSectionHeader 
            :title="showArchives ? 'Événements passés' : 'Agenda'"
            :subtitle="showArchives ? 'Il est trop tard pour ceux-là, mais l\'histoire reste !' : 'Ne manquez pas nos prochains rendez-vous.'"
            class="mb-0!"
          />
          
          <div class="flex flex-wrap items-center gap-3" role="group" aria-label="Filtres agenda">
            <template v-if="!showArchives">
              <!-- Mobile Filter -->
              <div class="lg:hidden w-full">
                <UiMobileSelect 
                  v-model="activeAgendaFilter"
                  :options="agendaFilters"
                  label="Filtrer l'agenda"
                />
              </div>

              <!-- Desktop Filter -->
              <div class="hidden lg:flex items-center gap-2">
                <UiTabs 
                  :items="agendaFilters" 
                  v-model="activeAgendaFilter"
                  size="sm"
                />
              </div>
            </template>
            
            <template v-else>
              <div class="flex flex-wrap gap-2">
                <div class="w-40">
                  <UiMobileSelect 
                    v-model="selectedArchiveYear"
                    :options="[{ label: 'Toutes les années', value: undefined }, ...archiveYears.map(y => ({ label: String(y), value: y }))]"
                    placeholder="Année"
                    label="Filtrer les archives par année"
                  />
                </div>
                <div class="w-48">
                  <UiMobileSelect 
                    v-model="selectedArchiveMonth"
                    :options="[{ label: 'Tous les mois', value: undefined }, ...months.map((m, idx) => ({ label: m, value: idx }))]"
                    placeholder="Mois"
                    label="Filtrer les archives par mois"
                  />
                </div>
              </div>
            </template>

            <div class="w-px h-8 bg-oxo-border hidden md:block mx-2"></div>
            
            <button 
              @click="toggleArchives"
              class="px-4 py-2 rounded-lg font-bold text-sm border border-oxo-border transition-all duration-300"
              :class="showArchives ? 'bg-foreground text-background' : 'bg-oxo-card-bg text-foreground-dim hover:text-foreground'"
              :aria-pressed="showArchives"
            >
              <i :class="['fas', showArchives ? 'fa-calendar-alt' : 'fa-history', 'mr-2']" aria-hidden="true"></i>
              {{ showArchives ? 'Agenda actuel' : 'Trop tard' }}
            </button>
          </div>
        </header>

        <ul v-if="filteredEvents.length > 0" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6 list-none p-0">
          <li v-for="event in filteredEvents" :key="event.id" class="w-full">
            <NuxtLink 
              :to="`/actus/evenement/${event.slug}`"
              class="group block bg-oxo-card-bg rounded-xl md:rounded-2xl overflow-hidden border border-oxo-border transition-all duration-500 hover:border-oxo-red/50 hover:shadow-2xl hover:shadow-oxo-red/5 h-full"
              :class="{ 'grayscale opacity-80 hover:grayscale-0 hover:opacity-100': showArchives }"
            >
              <article class="flex flex-col h-full">
                <div class="relative h-28 md:h-44 overflow-hidden">
                  <NuxtImg loading="lazy"
                    :src="event.image"
                    :alt="event.title"
                    class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    sizes="50vw md:33vw lg:25vw xl:20vw"
                  />
                  <div class="absolute top-2 left-2 md:top-4 md:left-4 z-10 bg-slate-950/90 backdrop-blur-md px-1.5 py-1 md:px-3 md:py-2 rounded-lg md:rounded-xl text-center shadow-2xl border border-white/10 group-hover:scale-105 transition-transform duration-500">
                    <div :class="['text-oxo-red font-black leading-none tracking-tighter', getEventDateParts(event).isRange ? 'text-xs md:text-lg' : 'text-sm md:text-2xl']">{{ getEventDateParts(event).day }}</div>
                    <div class="text-white font-black text-[10px] uppercase tracking-widest mt-0.5">{{ getEventDateParts(event).month }}</div>
                  </div>
                  <div class="absolute top-2 right-2 md:top-4 md:right-4 z-10">
                    <span :class="`px-2 py-0.5 md:px-3 md:py-1 text-[10px] font-black uppercase rounded-full shadow-md ${getCategoryColor(event.type)}`">
                      {{ event.type }}
                    </span>
                  </div>
                </div>

                <div class="p-3 md:p-5 flex flex-col grow">
                  <div class="flex flex-wrap items-center text-foreground-dim text-[10px] font-mono mb-2 md:mb-3 gap-2 md:gap-3">
                    <span class="flex items-center gap-1"><i class="far fa-clock text-oxo-red" aria-hidden="true"></i> {{ event.time }}</span>
                    <span v-if="event.endDateFormatted && !getEventDateParts(event).isRange" class="flex items-center gap-1">
                      <i class="far fa-calendar text-oxo-red" aria-hidden="true"></i>
                      {{ event.date }} - {{ event.endDateFormatted }}
                    </span>
                  </div>

                  <h3 class="font-display font-bold text-sm md:text-lg text-foreground mb-1.5 md:mb-2 group-hover:text-oxo-red transition-colors line-clamp-2 leading-tight">
                    {{ event.title }}
                  </h3>
                  
                  <p class="text-foreground-dim text-xs mb-3 md:mb-4 grow line-clamp-2 leading-relaxed">
                    {{ stripHtml(event.description) }}
                  </p>

                  <div class="pt-2 md:pt-3 border-t border-oxo-border/50 flex items-center justify-between mt-auto">
                    <span class="text-oxo-red font-bold text-[10px] md:text-xs group-hover:translate-x-1 transition-transform">
                      {{ showArchives ? 'Passé' : 'Détails' }} <i class="fas fa-arrow-right ml-0.5" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
              </article>
            </NuxtLink>
          </li>
        </ul>

        <div v-else class="text-center py-24 bg-oxo-card-bg/20 rounded-3xl border border-dashed border-oxo-border">
          <i class="far fa-calendar-times text-5xl text-foreground-dim/30 mb-6" aria-hidden="true"></i>
          <h3 class="text-2xl font-bold text-foreground mb-2">Aucun événement</h3>
          <p class="text-foreground-dim">Essayez de changer les filtres ou de revenir plus tard.</p>
          <button v-if="showArchives" @click="selectedArchiveYear = undefined; selectedArchiveMonth = undefined" class="mt-6 text-oxo-red font-bold text-sm underline underline-offset-4">
            Réinitialiser les filtres
          </button>
        </div>
      </div>
    </section>

    <!-- SECTION: NEWS -->
    <section class="py-section-py relative bg-oxo-card-bg/30" aria-label="Actualités de l'école">
      <div class="container mx-auto px-container-px">
        <header class="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <UiSectionHeader 
            title="Actualités"
            subtitle="Toute la vie de l'école et du réseau OXO."
            class="mb-0!"
          />

          <!-- Yearly & Category Filter -->
          <div class="flex flex-wrap items-center gap-6" role="search" aria-label="Filtres actualités">
            <!-- Year Filter -->
            <UiTabs 
              :items="['Toutes', ...newsYears.map(String)]" 
              :model-value="selectedYear ? String(selectedYear) : 'Toutes'"
              @update:model-value="(val: string) => changeYear(val === 'Toutes' ? undefined : Number(val))"
              size="sm"
            />

            <div class="w-px h-12 bg-oxo-border hidden lg:block mx-2"></div>

            <!-- Category Filter -->
            <div class="flex flex-col gap-2">
              <span class="text-[10px] font-black uppercase tracking-widest text-foreground-dim ml-2">Catégorie</span>
              <div class="w-48">
                <UiMobileSelect 
                  v-model="activeCategory"
                  :options="categories"
                  label="Filtrer les actualités par catégorie"
                />
              </div>
            </div>
          </div>
        </header>

        <div v-if="filteredNews.length > 0">
          <!-- 1. Featured News Layout (Large) -->
          <NuxtLink 
            v-if="filteredNews[0]"
            :to="`/actus/${filteredNews[0].slug}`"
            class="group block relative mb-16 md:mb-24 bg-oxo-card-bg rounded-2xl md:rounded-3xl overflow-hidden border border-oxo-border shadow-2xl hover:shadow-oxo-red/5 transition-all duration-500"
          >
            <article class="grid grid-cols-1 lg:grid-cols-2 gap-0 relative">
              <div class="relative h-[200px] md:h-[400px] lg:h-auto overflow-hidden">
                <NuxtImg loading="lazy"
                  :src="filteredNews[0].image"
                  :alt="filteredNews[0].title"
                  class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-1000"
                  sizes="100vw lg:50vw"
                />
                <div class="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500"></div>
                <div class="absolute top-4 left-4 md:top-6 md:left-6">
                  <span :class="`px-3 py-1.5 md:px-5 md:py-2 rounded-lg text-[10px] md:text-xs font-black uppercase tracking-widest shadow-xl ${getCategoryColor(filteredNews[0].category)}`">
                    {{ filteredNews[0].category }}
                  </span>
                </div>
              </div>

              <div class="p-6 md:p-8 lg:p-12 flex flex-col justify-center bg-background">
                <div class="flex items-center gap-2 md:gap-3 text-oxo-red text-xs md:text-sm font-black mb-4 md:mb-6">
                  <i class="far fa-calendar" aria-hidden="true"></i>
                  <time>{{ filteredNews[0].date }}</time>
                </div>

                <h2 class="font-display font-black text-2xl md:text-3xl lg:text-4xl text-foreground mb-4 md:mb-6 leading-tight group-hover:text-oxo-red transition-colors duration-300">
                  {{ filteredNews[0].title }}
                </h2>
                
                <p class="text-foreground-dim text-sm md:text-base lg:text-lg leading-relaxed mb-6 md:mb-10 line-clamp-3">
                  {{ filteredNews[0].excerpt }}
                </p>
                
                <div class="mt-auto">
                  <span class="inline-flex items-center gap-2 md:gap-3 px-4 py-2 md:px-6 md:py-3 bg-oxo-red text-white font-black text-xs md:text-sm rounded-xl transform group-hover:translate-x-2 transition-all shadow-lg shadow-oxo-red/20">
                    Lire l'article
                    <i class="fas fa-arrow-right" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
            </article>
          </NuxtLink>

          <!-- 2. Secondary Grid (4 medium items) -->
          <ul v-if="filteredNews.length > 1" class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-8 mb-16 md:mb-24 list-none p-0">
            <li v-for="item in filteredNews.slice(1, 6)" :key="item.id">
              <NuxtLink 
                :to="`/actus/${item.slug}`"
                class="group block h-full bg-oxo-card-bg rounded-xl md:rounded-2xl overflow-hidden border border-oxo-border hover:border-oxo-red/50 transition-all duration-500"
              >
                <article class="flex flex-col h-full">
                  <div class="aspect-video relative overflow-hidden">
                    <NuxtImg loading="lazy"
                      :src="item.image"
                      :alt="item.title"
                      class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      sizes="50vw lg:25vw xl:20vw"
                    />
                    <div class="absolute top-2 right-2 md:top-3 md:right-3">
                      <span :class="`px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-sm text-[10px] font-black uppercase ${getCategoryColor(item.category)}`">
                        {{ item.category }}
                      </span>
                    </div>
                  </div>
                  <div class="p-3 md:p-5 flex flex-col grow">
                    <time class="text-[10px] text-oxo-red font-black mb-1 md:mb-2 uppercase">{{ item.date }}</time>
                    <h3 class="font-display font-bold text-xs md:text-base text-foreground group-hover:text-oxo-red transition-colors line-clamp-2 leading-tight mb-2 md:mb-3">
                      {{ item.title }}
                    </h3>
                    <p class="text-foreground-dim text-xs line-clamp-2 mb-3 md:mb-4">
                      {{ item.excerpt }}
                    </p>
                    <div class="mt-auto text-oxo-red text-[10px] font-black flex items-center gap-1.5 md:gap-2">
                      Lire plus <i class="fas fa-arrow-right" aria-hidden="true"></i>
                    </div>
                  </div>
                </article>
              </NuxtLink>
            </li>
          </ul>

          <!-- 3. List Layout (Rest of the articles) -->
          <ul v-if="filteredNews.length > 5" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0 mt-12">
            <li v-for="item in filteredNews.slice(5)" :key="item.id">
              <NuxtLink 
                :to="`/actus/${item.slug}`"
                class="group block p-4 rounded-xl bg-oxo-card-bg/20 hover:bg-oxo-card-bg/50 transition-colors border border-oxo-border/40 hover:border-oxo-red/30"
              >
                <article class="flex items-center gap-6">
                  <div class="w-24 h-16 rounded-lg overflow-hidden shrink-0 relative">
                    <NuxtImg loading="lazy"
                      :src="item.image"
                      :alt="item.title"
                      class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      sizes="96px"
                    />
                  </div>
                  <div class="grow min-w-0">
                    <div class="flex items-center gap-3 mb-1.5 pl-0.5">
                      <time class="text-[10px] text-oxo-red font-black uppercase tracking-wider">{{ item.date }}</time>
                      <span class="text-[10px] text-foreground-dim font-bold px-2 py-0.5 rounded-sm bg-background/50 border border-oxo-border/50 capitalize">{{ item.category }}</span>
                    </div>
                    <h4 class="font-display font-bold text-base text-foreground group-hover:text-oxo-red transition-colors line-clamp-1 pr-4">
                      {{ item.title }}
                    </h4>
                  </div>
                  <i class="fas fa-angle-right text-oxo-red/50 group-hover:text-oxo-red transform group-hover:translate-x-1 transition-all mr-2" aria-hidden="true"></i>
                </article>
              </NuxtLink>
            </li>
          </ul>
          
          <!-- "Voir plus" Button -->
          <div v-if="page < totalPages" class="mt-24 text-center">
            <button 
              @click="loadMoreNews"
              :disabled="loadingNews"
              class="group relative inline-flex items-center gap-3 px-10 py-4 bg-background border-2 border-oxo-red text-oxo-red font-black clip-button hover:bg-oxo-red hover:text-white transition-all duration-300 disabled:opacity-50 shadow-xl shadow-oxo-red/5"
            >
              <i v-if="loadingNews" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
              <span v-else>Plus d'actualités</span>
              <i class="fas fa-plus transform group-hover:rotate-90 transition-transform" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div v-else class="text-center py-24 bg-background/50 rounded-3xl border border-oxo-border">
          <i class="fas fa-search text-5xl text-oxo-border mb-6" aria-hidden="true"></i>
          <h3 class="text-2xl font-black text-foreground mb-2">Aucun article trouvé</h3>
          <p class="text-foreground-dim italic">Essayez de changer l'année ou la catégorie.</p>
        </div>
      </div>
    </section>
    
    <AppCTA
      title="REJOIGNEZ"
      titleHighlight="L'AVENTURE"
      subtitle="Prêt à transformer votre passion en métier ? Nos inscriptions pour la session 2026 sont ouvertes."
      buttonText="DÉPOSER MON DOSSIER"
      buttonLink="/candidature"
    />
  </div>
</template>

<style scoped>
/* Snap scroll for carousel */
.snap-start {
  scroll-snap-align: start;
}
</style>

<style scoped>
</style>
